import React from 'react';
import { ArrowLeft } from '@lifeomic/chromicons';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { IconButtonLink } from '@lifeomic/chroma-react/components/IconButtonLink';
import { useIntl, defineMessages } from 'react-intl';
import { alpha } from '@mui/material/styles';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
  },
  backIcon: {
    top: '-1px',
    left: '-32px',
    padding: '8px',
    position: 'absolute',
    marginRight: '4px',
  },
  roundIcon: {
    marginRight: theme.spacing(2),
    backgroundColor: alpha(theme.palette.primary.main, 0.075),
    border: `solid 1px ${theme.palette.common.white}`,
    borderRadius: theme.pxToRem(9999),
    color: theme.palette.primary.main,
    height: theme.pxToRem(32),
    width: theme.pxToRem(32),
    minHeight: theme.pxToRem(32),
    minWidth: theme.pxToRem(32),
    '@media screen and (max-width: 480px)': {
      marginTop: 0,
      marginBottom: theme.spacing(2),
    },
  },
  description: {
    marginTop: theme.spacing(0.5),
    maxWidth: theme.pxToRem(908),
  },
  noResourceLinksText: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    height: theme.pxToRem(40),
  },
  resourceLinksContainer: {
    flexWrap: 'wrap',
    margin: theme.spacing(1, 0, 0, -1.25),
  },
}));

const messages = defineMessages({
  back: {
    id: 'detailsHeader.back',
    defaultMessage: 'Back',
  },
});

interface DetailsHeaderProps {
  backUrl?: string;
  description?: string;
  icon: React.MemoExoticComponent<
    (props: React.SVGProps<SVGSVGElement>) => JSX.Element
  >;
  noResourceLinksText?: string;
  resourceLinks?: any;
  showBackButton?: boolean;
  title: string;
}

const DetailsHeader: React.FC<DetailsHeaderProps> = ({
  backUrl,
  description,
  icon: Icon,
  noResourceLinksText,
  resourceLinks,
  showBackButton,
  title,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});

  return (
    <Box className={classes.root}>
      {showBackButton && (
        <IconButtonLink
          aria-label={formatMessage(messages.back)}
          className={classes.backIcon}
          icon={ArrowLeft}
          size={0}
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          to={backUrl}
        />
      )}
      <Box align="center" className={classes.roundIcon} justify="center">
        <Icon height={18} width={18} data-testid="header-icon" />
      </Box>
      <div>
        <Text size="headline" weight="bold">
          {title}
        </Text>
        {description && (
          <Text className={classes.description}>{description}</Text>
        )}
        {resourceLinks && (
          <Box
            className={classes.resourceLinksContainer}
            data-testid="resource-links"
          >
            {resourceLinks}
          </Box>
        )}
        {!resourceLinks && noResourceLinksText && (
          <Text className={classes.noResourceLinksText} size="caption">
            {noResourceLinksText}
          </Text>
        )}
      </div>
    </Box>
  );
};

export default DetailsHeader;
